import { AxiosRequestConfig } from 'axios';
import { InvoiceType } from './schemas/invoice.schema';
import { PlateType } from './schemas/plate-schema';

export enum PlateClassification {
  Unassigned = 0,
  Personal = 1,
  Business = 2
}

export type Client = {
  id: string;
  freshbooksId: string;
  name: string;
  emailAddress: string;
  phoneNumber: string;
  alternateEmailAddress: string;
  alternatePhoneNumber: string;
  recurringInvoiceFreshbooksId: string;
  monitoringListId: string;
  delinquent?: string;
  eligibleForTowing?: boolean;
  totalInterestOwed?: number;
  towingEligibilityLastCalculatedAt?: string;
  plates?: PlateType[];
  tickets?: ClientTicket[];
  cardPointeProfileId: string;
  nycAutoPay: boolean;
  ignoreCameraViolations: boolean;
  contactSecondaryEmails: ClientSecondaryEmails[];
};

export type FreshBookImportClient = {
  id: string;
  emailAddress: string;
  phoneNumber?: string;
  alternatePhoneNumber?: string;
  billingCity?: string;
  billingCountry?: string;
  billingPostCode?: string;
  billingState?: string;
  billingStreet?: string;
  name: string;
};

export type DeepClient = Client & {
  plates: Array<{ monitoringRange: MonitoringRange } & Plate>;
};

export type CombinedTicket = {
  internalTicket: InternalTicket;
  openDataTicket: OpenDataTicket;
};

export type CustomFetchInput<T> = {
  endpoint: string;
  processDescription?: string;
  callback?: (result?: T) => void;
  config?: RequestInit;
  skipParseBody?: boolean;
};
export type CustomAxiosInput<T> = {
  endpoint: string;
  processDescription?: string;
  config?: AxiosRequestConfig;
  callback?: (result?: T) => void;
  errorCallback?: (result?: any) => void;
  finalCallback?: () => void;
};

export type InternalTicket = {
  id: string;
  plateId: string;
  invoiceId?: string;
  invoice?: InvoiceType;
  summonsNumber: string;
  downloadURL?: string;
  settled: boolean;
  ignoreTicket?: boolean;
  paidToNycAt?: string;
  insertedAt: string;
};

export type ClientTicket = {
  id: string;
  plateNumber: string;
  dateIssued: string;
  summonsNumber: string;
  penaltyAmount: number;
  amountOutstanding: number;
  ignoreTicket: boolean;
  invoice?: InvoiceType;
  insertedAt: string;
  violationStatus?: string;
  downloadURL?: string;
};

export type ClientTicketList = {
  tickets: ClientTicket[];
  totalEstimates: number;
  totalInvoices: number;
  totalWaitingToBeInvoiced: number;
  totalDelinquent: number;
  totalWaitingToBePaidToNYC: number;
};

export type MonitoringList = {
  id: string;
  name: string;
  description: string;
  amount: number;
};

export type MonitoringRange = {
  id: string;
  startDate: string;
  endDate: string;
};

export type TicketListObject = {
  OpenDataTickets: OpenDataTicket[];
  isImport: boolean;
};

export type OpenDataTicket = {
  plate: string;
  state: string;
  license_type: string;
  summons_number: string;
  issue_date: string;
  fine_amount: number;
  amount_due: number;
  violation: string;
  violation_status?: string;
  interest_amount: string;
  penalty_amount: string;
};

export type DiscoveredTicket = OpenDataTicket & { newTicket: boolean };

export type Plate = {
  id: string;
  plateNumber: string;
  archivedAt?: string;
  clientId: string;
  monitoringRangeId?: string;
  monitoringRange?: MonitoringRange;
  tickets?: InternalTicket[];
  state: DropdownItem<string>;
  monitoringStartDate?: string;
  monitoringEndDate?: string;
  registrationExpirationDate?: string;
  registrationAddress?: string;
  classification: PlateClassification;
  classificationReviewedAt?: string | null;
  classificationReviewedByName?: string | null;
  businessNoticeSentAt?: string | null;
};

export type Process = {
  id: number;
  description: string;
};

export type Result = {
  process: string;
  results: any;
  backgroundColor?: string;
};

export type Job = {
  id: string;
  jobType: string;
  startTime: string;
  endTime: string;
  success: string;
  notes: string;
  shouldStop: string;
};

export type PaginatedJobs = {
  jobs: Job[];
  currentPage: number;
  numberOfPages: number;
};

export type WEFResponse<T> = {
  data: T;
  errorType: number;
  success: boolean;
  errors?: string[];
};

export type DropdownItem<T> = {
  value: T;
  label: string;
};

export type SavedCreditCards = {
  id: string;
  maskedCardNumber: string;
  cardType: string;
  expiry: string;
  isPrimary: boolean;
  addedAt: string | null;
  isExpired: boolean;
  isInCoolOffPeriod: boolean;
  lastFailedChargeAt: string | null;
  coolOffEndsAt: string | null;
};

export type ChangeLogs = {
  id: string;
  action: string;
  oldValue: string;
  newValue: string;
  propertyName: string;
  insertedBy: string;
  insertedAt: string;
};

export type PaginatedChangeLogs = {
  changeLogs: ChangeLogs[];
  currentPage: number;
  numberOfPages: number;
};

export type ChangeLogsMetadata = {
  entityId?: string;
  entityName?: string;
  showChangeLogs: boolean;
};

export type AnalyticsData = {
  totalTicketsWaitingForInvoicing?: number;
};

export type ClientTicketTabKeys =
  | 'estimates'
  | 'invoices'
  | 'delinquent'
  | 'waitingToBeInvoiced'
  | 'waitingToBePaidToNYC';

export type WaitingToBePaidToNycType = {
  clientId: string;
  clientName: string;
  waitingToBePaidToNycCount: number;
};

export type ClientSecondaryEmails = {
  id: string;
  clientId: string;
  email: string;
};

// Unassigned Plate Notification Types
export enum NotificationType {
  Email = 0,
  Sms = 1
}

export type ClientWithUnassignedPlates = {
  clientId: string;
  clientName: string;
  emailAddress: string;
  phoneNumber: string;
  plateNumbers: string[];
  ticketCount: number;
};

export type UnassignedPlateNotificationRequest = {
  clientIds: string[];
  notificationType: NotificationType;
};

export type NotificationPreview = {
  clientId: string;
  clientName: string;
  recipient: string;
  subject: string | null;
  message: string;
  notificationType: NotificationType;
  sendCount?: number;
};

export type SendNotificationResult = {
  clientId: string;
  clientName: string;
  success: boolean;
  errorMessage: string | null;
};

export type SendNotificationsResponse = {
  totalSent: number;
  totalFailed: number;
  results: SendNotificationResult[];
};

// SMS Log Types
export enum SmsType {
  ClientClassificationReminder = 0,
  PaymentReminder = 1,
  DelinquentNotice = 2,
  TicketStatusUpdate = 3,
  GeneralNotification = 4,
  TowingWarning = 5,
  BusinessPlateNotice = 6,
  RegistrationRequest = 7
}

export enum SmsStatus {
  Pending = 0,
  Success = 1,
  Failed = 2
}

export type SmsLog = {
  id: string;
  clientId: string;
  phoneNumber: string;
  smsType: string;
  status: string;
  sentAt: string;
  sendCount: number;
  errorMessage: string | null;
  messageContent: string;
};

// Towing Notification Types
export type TowingEligiblePlate = {
  plateId: string;
  plateNumber: string;
  interestOwed: number;
  unpaidTicketCount: number;
  isEligibleForTowing: boolean;
};

export type TowingEligibleClient = {
  clientId: string;
  clientName: string;
  emailAddress: string;
  phoneNumber: string;
  totalInterestOwed: number;
  plateNumbers: string[];
  plates: TowingEligiblePlate[];
  unpaidTicketCount: number;
  lastTowingNotificationSentAt: string | null;
  monitoringListId: string;
  monitoringListName: string;
};

export type TowingNotificationRequest = {
  clientIds: string[];
  notificationType: NotificationType;
};

export type TowingNotificationPreview = {
  clientId: string;
  clientName: string;
  recipient: string;
  subject: string | null;
  message: string;
  notificationType: NotificationType;
  totalInterestOwed: number;
  sendCount: number;
};
